import React, { useEffect, useState, useMemo } from 'react'
import { dateFormat } from 'helpers/date'
import AsideFilterStore from './asideFilterStore'
import SelectCustom from 'components/ContentBox/Global/Select/SelectCustom'
import { SuperButton } from 'components/UI/SuperButton/SuperButton'
import { CloseButton } from 'components/UI/SuperButton/SuperButton'
import FormInput from 'components/UI/AsideModal/Form/FormInput'
import RadioButton from 'components/UI/RadioButtons/RadioButton'
import FormField from '../AsideModal/Form/FormField'
import FormLabel from '../AsideModal/Form/FormLabel'
import styles from './AsideFilter.module.scss'
import Checkbox from 'components/ContentBox/Global/Checkbox/Checkbox'
import CustomDatePicker from '../CustomDatePicker/CustomDatePicker'
import ToggleCheckbox from '../ToggleCheckbox/ToggleCheckbox'

const AsideFilter = ({
  config,
  filterValue,
  getFilterParams,
  onDropFilter,
  translate,
  showLabel = true,
}) => {
  const { closeFilter, dropTextValues, filterTextValues } = AsideFilterStore

  const [inputsValues, setInputsValues] = useState(filterValue || {})
  const [textValues, setTextValues] = useState(filterTextValues || [])
  const [errorFields, setErrorFields] = useState([])
  const [autoSelectedValues, setAutoSelectedValues] = useState({})

  // Состояние для перерендера SelectCustom при изменении зависимостей
  const [selectKey, setSelectKey] = useState(0)

  const validateInputs = () => {
    const rules = new Map([
      ['select', (item) => item.isRequired && !inputsValues[item.nameState]],
      [
        'select-multi-dynamic',
        (item) =>
          item.isRequired &&
          (!inputsValues[item.nameState] ||
            inputsValues[item.nameState].length === 0),
      ],
      [
        'date_day_picker',
        (item) =>
          item.isRequired &&
          !inputsValues[item.nameState[0]] &&
          !inputsValues[item.nameState[1]],
      ],
      [
        'select-multi',
        (item) =>
          item.isRequired &&
          (!inputsValues[item.nameState] ||
            inputsValues[item.nameState].length === 0),
      ],
    ])
    const errorFields = config
      .filter((item) => rules.get(item.type)?.(item))
      .map((item) => item.nameState)
    setErrorFields(errorFields)
    return errorFields
  }

  const getFilterValue = () => {
    const validationResult = validateInputs()
    if (validationResult.length > 0) return
    closeFilter()
    AsideFilterStore.setTextValues(textValues)
    if (getFilterParams) getFilterParams(inputsValues)
  }

  const dropFilter = () => {
    setInputsValues({})
    dropTextValues()
    if (onDropFilter) onDropFilter()
  }

  const returnValue = (key) => inputsValues[key] ?? ''

  const changeHandler = (e, key, item) => {
    const value = e?.target ? e.target.value : e
    setInputsValues({ ...inputsValues, [key]: value })

    // Если изменилось поле, от которого зависят другие - обновляем ключ для перерендера
    if (item?.dependsOn || key === item?.dependsOn) {
      setSelectKey((prev) => prev + 1)
    }

    if (item?.onRealTimeChange && key === 'location') {
      item.onRealTimeChange(value)

      if (item.autoSelectedValues && item.autoSelectedValues.length > 0) {
        setInputsValues((prev) => ({
          ...prev,
          employee_id: item.autoSelectedValues.map(
            (teacher) => teacher.id || teacher,
          ),
        }))
      }
    }
  }

  useEffect(() => {
    const newAutoSelectedValues = {}
    config.forEach((item) => {
      if (item.autoSelectedValues) {
        newAutoSelectedValues[item.nameState] = item.autoSelectedValues

        if (item.type === 'select-multi') {
          setInputsValues((prev) => ({
            ...prev,
            [item.nameState]: item.autoSelectedValues.map(
              (teacher) => teacher.id || teacher,
            ),
          }))
        }
      }
    })
    setAutoSelectedValues(newAutoSelectedValues)
  }, [config])

  const changeTextValuesHandler = (key, label, value) => {
    const field = textValues.find((x) => x.key === key)
    if (field)
      setTextValues(
        textValues.map((field) =>
          field.key === key ? { key, label, value } : field,
        ),
      )
    else setTextValues([...textValues, { key, label, value }])
  }

  const handleDateChange = (date, key, label) => {
    const formattedDate = dateFormat(date, 'YYYY-mm-dd')
    changeHandler(formattedDate, key)
    changeTextValuesHandler(key, label, formattedDate)
  }

  const changeTextValuesHandlerMulti = (key, label, value) => {
    const field = textValues.find((x) => x.key === key)
    const values = value.map((el) => el.label).join(', ')
    if (field) {
      setTextValues(
        textValues.map((field) =>
          field.key === key ? { key, label, value: values } : field,
        ),
      )
    } else setTextValues([...textValues, { key, label, value: values }])
  }

  const objectChangeHandler = (value, key_name, subMenu_key) => {
    if (subMenu_key !== undefined) {
      setInputsValues({
        ...inputsValues,
        [key_name]: value,
        [subMenu_key]: subMenu_key,
        is_active: true,
      })
    } else {
      setInputsValues({ ...inputsValues, [key_name]: value })
    }
  }

  // Функция для получения динамических параметров API
  const getApiConfParams = (item) => {
    if (!item.apiConfParams) return undefined

    const params = { ...item.apiConfParams }
    if (item.dependsOn && inputsValues[item.dependsOn]) {
      Object.keys(params).forEach((key) => {
        if (typeof params[key] === 'string' && params[key] === item.dependsOn) {
          params[key] = inputsValues[item.dependsOn]
        }
      })
    }
    return params
  }

  // Проверка зависимости для disabled
  const isFieldDisabled = (item) => {
    return item.dependsOn && !inputsValues[item.dependsOn]
  }

  const renderFilterFields = () => {
    const selectArr = []
    config.forEach((item, index) => {
      switch (item.type) {
        case 'select':
          selectArr.push(
            <FormField key={index}>
              <FormLabel
                isRequired={item.isRequired || false}
                showLabel={showLabel}
              >
                {item.label}:
              </FormLabel>
              <SelectCustom
                key={`select-${index}-${selectKey}`}
                placeholder={item.placeholder}
                apiLink={item.apiLink}
                apiLinkCRM={item.apiLinkCRM}
                apiConfParams={getApiConfParams(item)}
                apiGetResult={
                  item.key_name
                    ? (data) => data[item.key_name].items
                    : item?.apiGetResult
                }
                valueID={returnValue(item.nameState)}
                onChange={(o) => {
                  changeHandler({ target: o }, item.nameState, item)
                  changeTextValuesHandler(item.nameState, item.label, o.label)
                }}
                isSearchable={item?.isSearch}
                options={item?.values}
                optionLabelKey={item?.option_label_key}
                optionValueKey={item?.option_value_key}
                formatOptionLabel={item?.formatOptionLabel}
                error={errorFields.includes(item.nameState)}
                isDisabled={isFieldDisabled(item)}
              />
            </FormField>,
          )
          break
        case 'select_father':
          selectArr.push(
            <FormField key={index}>
              <FormLabel showLabel={showLabel || item.showLabel}>
                {item.label}:
              </FormLabel>
              <SelectCustom
                placeholder={item.placeholder}
                apiLink={item.apiLink}
                apiGetResult={
                  item.key_name ? (data) => data[item.key_name].items : null
                }
                valueID={inputsValues[item.nameState]?.id}
                optionLabelKey={item.option_label_key}
                onChange={(o) =>
                  setInputsValues({ ...inputsValues, [item.nameState]: o })
                }
                isSearchable
              />
            </FormField>,
          )
          break
        case 'select_son':
          selectArr.push(
            <FormField key={index}>
              <FormLabel showLabel={showLabel || item.showLabel}>
                {item.label}:
              </FormLabel>
              <SelectCustom
                placeholder={item.placeholder}
                valueID={inputsValues[item.nameState]}
                options={inputsValues[item.fatherName]?.[item.optionsKey] || []}
                optionLabelKey={item.option_label_key}
                onChange={(o) => changeHandler(o.id, item.nameState)}
                isSearchable
              />
            </FormField>,
          )
          break
        case 'select_father_custom':
          selectArr.push(
            <FormField key={index}>
              <FormLabel showLabel={showLabel || item.showLabel}>
                {item.label}:
              </FormLabel>
              <SelectCustom
                placeholder={item.placeholder}
                apiLink={item.apiLink}
                apiGetResult={(data) => {
                  const result = data.result || data
                  return result
                }}
                valueID={inputsValues[item.nameState]}
                optionLabelKey={item.option_label_key || 'name'}
                optionValueKey={item.option_value_key || 'id'}
                onChange={(o) => {
                  const selectedId = o ? o.id || o.value : null
                  const selectedObject = o || null

                  const newInputsValues = { ...inputsValues }

                  if (selectedId) {
                    newInputsValues[item.nameState] = selectedId
                    newInputsValues[`${item.nameState}_data`] = selectedObject
                  } else {
                    delete newInputsValues[item.nameState]
                    delete newInputsValues[`${item.nameState}_data`]
                  }

                  delete newInputsValues[item.sonField]

                  setInputsValues(newInputsValues)

                  if (selectedId && o) {
                    changeTextValuesHandler(
                      item.nameState,
                      item.label,
                      o.label || o.name,
                    )
                  } else {
                    const updatedTextValues = textValues.filter(
                      (tv) => tv.key !== item.nameState,
                    )
                    setTextValues(updatedTextValues)
                  }
                }}
                isSearchable
              />
            </FormField>,
          )
          break

        case 'select_son_custom':
          const parentData = inputsValues[`${item.fatherName}_data`]
          const sonOptions = parentData?.[item.optionsKey] || []

          selectArr.push(
            <FormField key={index}>
              <FormLabel showLabel={showLabel || item.showLabel}>
                {item.label}:
              </FormLabel>
              <SelectCustom
                placeholder={item.placeholder}
                valueID={inputsValues[item.nameState]}
                options={sonOptions}
                optionLabelKey={item.option_label_key || 'name'}
                optionValueKey={item.option_value_key || 'id'}
                onChange={(o) => {
                  const selectedId = o ? o.id || o.value : null

                  const newInputsValues = { ...inputsValues }

                  if (selectedId) {
                    newInputsValues[item.nameState] = selectedId
                    changeTextValuesHandler(
                      item.nameState,
                      item.label,
                      o.label || o.name,
                    )
                  } else {
                    delete newInputsValues[item.nameState]
                    const updatedTextValues = textValues.filter(
                      (tv) => tv.key !== item.nameState,
                    )
                    setTextValues(updatedTextValues)
                  }

                  setInputsValues(newInputsValues)
                }}
                isSearchable
                isDisabled={!inputsValues[item.fatherName]}
                noOptionsMessage={() =>
                  !inputsValues[item.fatherName]
                    ? 'Please select department first'
                    : 'No options available'
                }
              />
            </FormField>,
          )
          break
        case 'select-multi-dynamic':
          const apiConfParams = getApiConfParams(item)
          const isDisabled = isFieldDisabled(item)

          // Если поле disabled - передаем undefined вместо apiConfParams, чтобы не делать запрос
          const finalApiConfParams = isDisabled ? undefined : apiConfParams

          selectArr.push(
            <FormField key={index}>
              <FormLabel
                showLabel={showLabel}
                isRequired={item.isRequired || false}
              >
                {item.label}:
              </FormLabel>
              <SelectCustom
                key={`select-multi-dynamic-${index}-${selectKey}-${inputsValues[item.dependsOn] || 'none'}`}
                placeholder={item.placeholder}
                apiLink={item.apiLink}
                apiLinkCRM={item.apiLinkCRM}
                apiConfParams={finalApiConfParams}
                apiGetResult={
                  item.apiGetResult ||
                  (item.key_name ? (data) => data[item.key_name].items : null)
                }
                valueID={inputsValues[item.nameState] || []}
                onChange={(opts) => {
                  setInputsValues({
                    ...inputsValues,
                    [item.nameState]: opts.map((opt) => opt.id),
                  })
                  changeTextValuesHandlerMulti(item.nameState, item.label, opts)
                }}
                isSearchable={item?.isSearch}
                options={item?.values}
                optionLabelKey={item?.option_label_key || 'name'}
                optionValueKey={item?.option_value_key || 'id'}
                isMulti
                error={errorFields.includes(item.nameState)}
                isDisabled={isDisabled}
                noOptionsMessage={() =>
                  isDisabled
                    ? 'Сначала выберите воронку'
                    : 'Нет доступных статусов'
                }
              />
            </FormField>,
          )
          break
        case 'select-multi':
          selectArr.push(
            <FormField key={index}>
              <FormLabel
                showLabel={showLabel}
                isRequired={item.isRequired || false}
              >
                {item.label}:
              </FormLabel>
              <SelectCustom
                placeholder={item.placeholder}
                apiLink={item.apiLink}
                apiLinkCRM={item.apiLinkCRM}
                apiConfParams={item.apiConfParams}
                apiGetResult={
                  item.key_name ? (data) => data[item.key_name].items : null
                }
                valueID={inputsValues[item.nameState] || []}
                onChange={(opts) => {
                  setInputsValues({
                    ...inputsValues,
                    [item.nameState]: opts.map((opt) => opt.id),
                  })
                  changeTextValuesHandlerMulti(item.nameState, item.label, opts)
                }}
                isSearchable={item?.isSearch}
                options={item?.values}
                optionLabelKey={item?.option_label_key}
                isMulti
                error={errorFields.includes(item.nameState)}
              />
            </FormField>,
          )
          break
        case 'select_request':
          selectArr.push(
            <FormField key={index}>
              <FormLabel showLabel={showLabel || item.showLabel}>
                {item.label}:
              </FormLabel>
              <SelectCustom
                placeholder={item?.placeholder}
                apiLink={item.apiLink}
                apiGetResult={
                  item?.key_name
                    ? (data) => data[item?.key_name].items
                    : item?.apiGetResult
                }
                sortKey={item?.sort_key}
                valueID={inputsValues[item?.nameState] || []}
                optionValueKey={item?.option_value_key}
                optionLabelKey={item?.option_label_key}
                apiConfParams={item?.params}
                onChange={(o) => {
                  changeHandler(
                    item?.option_value_key ? o[item?.option_value_key] : o.id,
                    item?.nameState,
                  )
                }}
                isSearchable
              />
            </FormField>,
          )
          break
        case 'input':
          selectArr.push(
            <FormField key={index}>
              <FormLabel showLabel={showLabel || item.showLabel}>
                {item.label}:
              </FormLabel>
              <FormInput>
                <input
                  type={item.type || 'text'}
                  placeholder={item.placeholder}
                  value={returnValue(item.nameState) || ''}
                  onChange={(e) => {
                    changeHandler(e, item.nameState)
                    changeTextValuesHandler(
                      item.nameState,
                      item.label,
                      e.target.value,
                    )
                  }}
                  className='filter-box__input'
                />
              </FormInput>
            </FormField>,
          )
          break
        case 'type_selector':
          selectArr.push(
            <FormField key={index}>
              <FormLabel showLabel={showLabel || item.showLabel}>
                {item.label}:
              </FormLabel>
              <RadioButton
                values={item.values}
                like_obj={true}
                objectChangeHandler={(e, key) => {
                  if (item.query_key) {
                    const selectedItem = item.values.find((x) => x.id === e)
                    changeHandler(selectedItem.key, key)
                    changeTextValuesHandler(
                      item.nameState,
                      item.label,
                      selectedItem.name,
                    )
                  } else {
                    changeHandler(e, key)
                    changeTextValuesHandler(
                      item.nameState,
                      item.label,
                      item.values.find((x) => x.id === e).name,
                    )
                  }
                }}
                selected={
                  item.query_key
                    ? item.values.find(
                        (x) => x.key === inputsValues[item.nameState],
                      )?.id
                    : returnValue(item.nameState)
                }
                name={item.nameState}
              />
            </FormField>,
          )
          break
        case 'date_day_picker':
          selectArr.push(
            <FormField key={index}>
              <FormLabel
                isRequired={item.isRequired || false}
                showLabel={showLabel}
              >
                {item.label}:
              </FormLabel>
              <div
                className={`${styles.filterField__montPicker} ${styles.filterField__monthRangePicker} ${
                  errorFields.includes(item.nameState)
                    ? styles.filterField__monthRangePicker__error
                    : ''
                }`}
              >
                <CustomDatePicker
                  selectedDate={
                    returnValue(item.nameState[0]) &&
                    new Date(returnValue(item.nameState[0]))
                  }
                  onChangeDate={(date) =>
                    handleDateChange(
                      date,
                      item.nameState[0],
                      translate.start_date,
                    )
                  }
                  maxDateInf={
                    returnValue(item.nameState[1]) &&
                    new Date(returnValue(item.nameState[1]))
                  }
                  name={translate?.start_date}
                />
                <CustomDatePicker
                  selectedDate={
                    returnValue(item.nameState[1]) &&
                    new Date(returnValue(item.nameState[1]))
                  }
                  onChangeDate={(date) =>
                    handleDateChange(
                      date,
                      item.nameState[1],
                      translate.end_date,
                    )
                  }
                  minDateInf={
                    returnValue(item.nameState[0]) &&
                    new Date(returnValue(item.nameState[0]))
                  }
                  name={translate?.end_date}
                />
              </div>
            </FormField>,
          )
          break
        case 'checkbox':
          selectArr.push(
            <div key={index}>
              <Checkbox
                label={item.label}
                checked={inputsValues[item.name]}
                name={item.name}
                onChange={() =>
                  objectChangeHandler(!inputsValues[item.name], item.name)
                }
              />
            </div>,
          )
          break
        case 'switcher':
          selectArr.push(
            <div key={index}>
              <ToggleCheckbox
                label={item.label}
                checked={inputsValues[item.name]}
                onChange={() =>
                  objectChangeHandler(!inputsValues[item.name], item.name)
                }
                withoutIcons
              />
            </div>,
          )
          break
        default:
          break
      }
    })
    return selectArr
  }

  const onKeyDown = (e) => {
    if (e.key === 'Enter') getFilterValue()
  }

  useEffect(() => {
    document.body.classList.add('modalOpen')
    return () => {
      document.body.classList.remove('modalOpen')
    }
  }, [])

  return (
    <div onClick={closeFilter} className={styles.wrapper}>
      <div
        className={styles.box}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}
      >
        <div className={styles.header}>
          <div className={styles.title}>{translate.filter}</div>
          <CloseButton onClick={closeFilter} />
        </div>
        <div className={styles.mobileWrapper}>
          <div className={styles.body}>{renderFilterFields()}</div>
          <div className={styles.btnBox}>
            <SuperButton onClick={dropFilter} variant='reset'>
              {translate.reset_all}
            </SuperButton>
            <SuperButton onClick={getFilterValue} variant='confirm'>
              {translate.apply}
            </SuperButton>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AsideFilter
